import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DeviceHubRoundedIcon from '@mui/icons-material/DeviceHubRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Grid,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import MainGrid from '../components/MainGrid';
import Dropzone from 'react-dropzone';
import { ERROR_STATE, ExternalAPIType } from '../Utils/Enums';
import {
	getAccessToken,
	getElderHomeId,
	getUserEmail,
	getUserId,
	getUserName,
} from '../Utils/AuthUtil';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
	deleteConfiguration,
	storeExternalApiConfig,
	testExternalApiConfig,
	updateExternalApiConfig,
} from '../services/ElderService';
import { fetchCareHome, fetchCareHomeConfigs } from '../services/UserService';
import ToastBanner from '../components/ToastBanner';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { dark, teal } from '../assets/theme/Colors';
import { Delete, Edit } from '@mui/icons-material';

const apiConnectCard = {
	name: 'External API Connected',
	status: 'Connected',
	color: '#16a34a',
	bg: '#dcfce7',
};

const API_INITIAL_STATE = {
	apiName: '',
	endpoint: '',
	method: '',
	authType: '',
	token: '',
	rootField: '',
	uniqueKey: '',
	fields: [
		{ key: 'elderId', value: '' },
		{ key: 'name', value: '' },
	],
	headers: [{ key: '', value: '' }],
	queryParams: [{ key: '', value: '' }],
	requestBody: '',
};

const CURL_API_INITIAL_STATE = {
	rootField: '',
	idKeyField: '',
	requestBody: '',
};

const API_ERROR_STATE = {
	apiName: ERROR_STATE,
	endpoint: ERROR_STATE,
	method: ERROR_STATE,
	authType: ERROR_STATE,
	token: ERROR_STATE,
	rootField: ERROR_STATE,
	uniqueKey: ERROR_STATE,
	fields: ERROR_STATE,
	headers: ERROR_STATE,
	queryParams: ERROR_STATE,
};

const CURL_API_ERROR_STATE = {
	rootField: ERROR_STATE,
	idKeyField: ERROR_STATE,
	requestBody: ERROR_STATE,
};

const INITIAL_ORG = {
	name: '',
	cqcNumber: '',
	address: '',
	adminEmail: '',
	primaryContact: '',
};

const INITIAL_API_CONFIGS = {
	baseUrl: '',
	authType: '',
	token: '',
	username: '',
	password: '',
	apiKey: '',
	clientType: '',
	headerName: '',
	headerValue: '',
};

const INITIAL_API_CONFIGS_ERROR = {
	baseUrl: '',
	authType: '',
	clientType: '',
};

function FormField({
	label,
	placeholder,
	value,
	onChange,
	type = 'text',
	disabled = false,
	error,
}) {
	return (
		<Box>
			<Typography
				sx={{
					fontSize: 12,
					fontWeight: 500,
					mb: 1,
					color: '#334155',
				}}>
				{label}
			</Typography>

			<TextField
				fullWidth
				size="small"
				type={type}
				onChange={onChange}
				value={value}
				placeholder={placeholder}
				sx={{
					'& .MuiInputBase-input': {
						fontSize: 13,
					},
				}}
				InputProps={{
					readOnly: disabled,
				}}
				error={error?.status}
			/>
			{error?.status && (
				<Typography
					sx={{
						fontSize: 12,
						fontWeight: 500,
						mt: 1,
						color: 'red',
					}}>
					{error?.msg}
				</Typography>
			)}
		</Box>
	);
}

export default function Settings() {
	const token = getAccessToken();
	const userId = getUserId();
	const homeId = getElderHomeId();
	const [fileData, setFileData] = useState('');
	const [txtError, setTxtError] = useState({ status: false, msg: '' });
	const [isLoading, setIsLoading] = useState(false);
	const [manualApiData, setManualApiData] = useState(API_INITIAL_STATE);
	const [manualApiErrors, setManualApiError] = useState(API_ERROR_STATE);
	const [loadingMsg, setLoadingMsg] = useState('');
	const [isGenerationComplete, setIsGenerationComplete] = useState(false);
	const [newConfigs, setNewConfigs] = useState(INITIAL_API_CONFIGS);
	const [newConfigsError, setNewConfigsError] = useState(
		INITIAL_API_CONFIGS_ERROR,
	);
	const [configs, setConfigs] = useState([]);
	const [orgData, setOrgData] = useState(INITIAL_ORG);
	const [isApiConfigured, setIsApiConfigured] = useState(false);
	const [showApiConfigForm, setShowApiConfigForm] = useState(false);

	useEffect(() => {
		fetchConfigs();
		fetchHome();
	}, []);

	async function fetchConfigs() {
		const response = await fetchCareHomeConfigs(homeId, token);

		if (response.message === 'Success') {
			setConfigs(response.content);
			setIsApiConfigured(true);
			setShowApiConfigForm(false);
		} else {
			console.error(response.message);
			setIsApiConfigured(false);
			setShowApiConfigForm(true);
		}
	}

	async function fetchHome() {
		const response = await fetchCareHome(homeId, token);

		if (response.message === 'Success') {
			const home = response.content;

			setOrgData(prevState => ({
				...prevState,
				name: home.name !== 'N/A' ? home.name : 'SenzCare Home',
				adminEmail: getUserEmail(),
				primaryContact: getUserName(),
				address: home.location,
			}));
		} else {
			console.error(response.message);
		}
	}

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['json'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		setTxtError({ status: false, msg: '' });
		setFileData(e[0]);
	}

	function validateConfigInputs() {
		let isValid = true;

		if (!newConfigs.baseUrl || newConfigs.baseUrl === '') {
			setNewConfigsError(prevState => ({
				...prevState,
				baseUrl: {
					status: true,
					msg: 'Enter base Url',
				},
			}));
			isValid = false;
		}

		if (!newConfigs.authType || newConfigs.authType === '') {
			setNewConfigsError(prevState => ({
				...prevState,
				authType: {
					status: true,
					msg: 'Select auth type',
				},
			}));
			isValid = false;
		}

		if (!newConfigs.clientType || newConfigs.clientType === '') {
			setNewConfigsError(prevState => ({
				...prevState,
				clientType: {
					status: true,
					msg: 'Enter client type',
				},
			}));
			isValid = false;
		}

		return isValid;
	}

	async function onStoreExternalApiDetails() {
		if (!validateConfigInputs()) {
			return;
		}

		setIsLoading(true);
		setLoadingMsg('Testing Connection');

		const body = {
			elderHomeId: homeId,
			adminId: userId,
			...newConfigs,
		};

		const response = await testExternalApiConfig(token, body);
		if (!response.error) {
			if (response.content.success) {
				setTimeout(() => {
					onStoreApiConfigs(body);
				}, 1000);
			} else {
				setIsGenerationComplete(false);
				setIsLoading(false);
				toast.error(response.content.message);
			}
		} else {
			toast.error(response.message);
			setIsLoading(false);
			setIsGenerationComplete(false);
			setLoadingMsg('');
		}
	}

	async function onUpdateExternalApiDetails() {
		if (!validateConfigInputs()) {
			return;
		}

		if (!configs.id) {
			toast.error('Configuration id not found');
			return;
		}

		setIsLoading(true);
		setLoadingMsg('Updating Configuration');

		const body = {
			elderHomeId: homeId,
			adminId: userId,
			...newConfigs,
			clientType: configs.clientType,
		};

		const response = await updateExternalApiConfig(configs.id, token, body);

		if (!response.error) {
			toast.success('Configuration updated successfully');
			setNewConfigs(INITIAL_API_CONFIGS);
			setShowApiConfigForm(false);
			fetchConfigs();
		} else {
			toast.error(response.message);
		}

		setIsLoading(false);
		setLoadingMsg('');
		setIsGenerationComplete(false);
	}

	async function onStoreApiConfigs(data) {
		setLoadingMsg('Obtaining Residents Data');

		const response = await storeExternalApiConfig(token, data);
		if (!response.error) {
			if (response.content.externalSyncSuccess) {
				setIsGenerationComplete(true);
				setLoadingMsg('Onboarding Successful');
				setNewConfigs(INITIAL_API_CONFIGS);
				fetchConfigs();
				setTimeout(() => {
					setIsLoading(false);
					setLoadingMsg('');
					setIsGenerationComplete(false);
				}, 1000);
			} else {
				setIsGenerationComplete(false);
				setIsLoading(false);
				toast.error(response.content.externalErrors[0]);
			}
		} else {
			setIsLoading(false);
			toast.error(response.message);
		}
	}

	async function removeConfigs() {
		const id = configs.id;
		if (!id) {
			return;
		}

		const response = await deleteConfiguration(id, token);

		if (!response.error) {
			toast.success('Configuration removed Successfully');
			setNewConfigs(INITIAL_API_CONFIGS);
			setShowApiConfigForm(true);
			fetchConfigs();
		} else {
			toast.error(response.message);
		}
	}

	function onEditConfigs() {
		setNewConfigs({
			...INITIAL_API_CONFIGS,
			baseUrl: configs.baseUrl ?? '',
			authType: configs.authType ?? '',
			token: configs.token ?? '',
			username: configs.username ?? '',
			password: configs.password ?? '',
			apiKey: configs.apiKey ?? '',
			clientType: configs.clientType ?? '',
			headerName: configs.headerName ?? '',
			headerValue: configs.headerValue ?? '',
		});
		setNewConfigsError(INITIAL_API_CONFIGS_ERROR);
		setShowApiConfigForm(true);
	}

	function onCancelConfigEdit() {
		setNewConfigs(INITIAL_API_CONFIGS);
		setNewConfigsError(INITIAL_API_CONFIGS_ERROR);
		setShowApiConfigForm(false);
	}

	const handleChange = (field, value) => {
		setOrgData(prev => ({
			...prev,
			[field]: value,
		}));
	};

	const handleAPIConfigChange = (field, value) => {
		setNewConfigs(prev => ({
			...prev,
			[field]: value,
		}));

		setNewConfigsError(prev => ({
			...prev,
			[field]: '',
		}));
	};

	return (
		<MainGrid>
			<Box
				sx={{
					width: '100%',
				}}>
				{/* Header */}
				<Box sx={{ mb: 3 }}>
					<Typography
						sx={{
							fontSize: '22px',
							fontWeight: 600,
							color: dark,
							mb: 0.5,
						}}>
						Settings
					</Typography>

					<Typography
						sx={{
							fontSize: 14,
							color: '#64748b',
						}}>
						Organization profile and API integration configuration
					</Typography>
				</Box>

				<Grid container spacing={3}>
					{/* Care Home Profile */}
					<Grid item xs={12} md={6}>
						<Card
							sx={{
								borderRadius: 4,
								border: '1px solid #e2e8f0',
								boxShadow: '0 2px 12px rgba(15,23,42,0.06)',
							}}>
							<CardContent sx={{ p: 3 }}>
								<Stack
									direction="row"
									spacing={1.2}
									alignItems="center"
									mb={2.5}>
									<ApartmentRoundedIcon
										sx={{
											fontSize: 16,
											color: teal,
										}}
									/>

									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 600,
											color: '#0f172a',
										}}>
										Care Home Profile
									</Typography>
								</Stack>
								<Stack spacing={2.5}>
									<FormField
										label="Organization Name"
										placeholder="SenzCare Home"
										value={orgData.name}
										onChange={e =>
											handleChange('name', e.target.value)
										}
										disabled={true}
									/>

									<FormField
										label="CQC Registration Number"
										placeholder="1-2345678901"
										value={orgData.cqcNumber}
										onChange={e =>
											handleChange(
												'cqcNumber',
												e.target.value,
											)
										}
										disabled={true}
									/>

									<FormField
										label="Address"
										placeholder="12 Meadow Lane, Leeds, LS1 4AB"
										value={orgData.address}
										onChange={e =>
											handleChange(
												'address',
												e.target.value,
											)
										}
										disabled={true}
									/>

									<FormField
										label="Admin Email"
										placeholder="admin@senzcare.com"
										value={orgData.adminEmail}
										onChange={e =>
											handleChange(
												'adminEmail',
												e.target.value,
											)
										}
										disabled={true}
									/>

									<FormField
										label="Primary Contact"
										placeholder="Admin"
										value={orgData.primaryContact}
										onChange={e =>
											handleChange(
												'primaryContact',
												e.target.value,
											)
										}
										disabled={true}
									/>

									{/* <Button
										variant="contained"
										startIcon={<SaveRoundedIcon />}
										sx={{
											width: '40%',
											borderRadius: '12px',
											backgroundColor: teal,
											textTransform: 'none',
											fontWeight: 600,
											fontSize: '13px',
											boxShadow: 'none',
											'&:hover': {
												backgroundColor: teal,
												boxShadow: 'none',
											},
											color: dark,
										}}>
										Save Profile
									</Button> */}
								</Stack>
							</CardContent>
						</Card>
					</Grid>

					{/* API Integrations */}
					<Grid item xs={12} md={6}>
						<Card
							sx={{
								borderRadius: 4,
								border: '1px solid #e2e8f0',
								boxShadow: '0 2px 12px rgba(15,23,42,0.06)',
							}}>
							<CardContent sx={{ p: 3 }}>
								<Stack
									direction="row"
									spacing={1.2}
									alignItems="center"
									mb={2.5}>
									<DeviceHubRoundedIcon
										sx={{
											fontSize: 16,
											color: teal,
										}}
									/>

									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 600,
											color: '#0f172a',
										}}>
										API Integrations
									</Typography>
								</Stack>

								<Stack spacing={1.5}>
									{isApiConfigured ? (
										<Box
											key={apiConnectCard.name}
											sx={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												p: 2.5,
												border: '1px solid',
												borderColor: 'divider',
												borderRadius: 3,
												bgcolor: 'background.paper',
												'&:hover': {
													boxShadow: 3,
													borderColor: 'primary.main',
												},
											}}>
											<Stack
												direction="row"
												spacing={2}
												alignItems="center">
												<Box
													sx={{
														width: 48,
														height: 48,
														borderRadius: '50%',
														bgcolor:
															apiConnectCard.bg,
														display: 'flex',
														alignItems: 'center',
														justifyContent:
															'center',
													}}>
													<Box
														sx={{
															width: 14,
															height: 14,
															borderRadius: '50%',
															bgcolor:
																apiConnectCard.color,
														}}
													/>
												</Box>

												<Box>
													<Typography
														fontWeight={600}
														fontSize={16}>
														{apiConnectCard.name}
													</Typography>

													<Stack
														spacing={0.3}
														mt={0.5}>
														<Typography
															variant="caption"
															color="text.secondary">
															<strong>
																Client:
															</strong>{' '}
															{configs.clientType}
														</Typography>

														<Typography
															variant="caption"
															color="text.secondary">
															<strong>
																Sync Status:
															</strong>{' '}
															<Box
																component="span"
																sx={{
																	fontWeight: 600,
																	color:
																		configs.lastSyncStatus ===
																		'SUCCESS'
																			? 'success.main'
																			: configs.lastSyncStatus ===
																				  'FAILED'
																				? 'error.main'
																				: 'text.secondary',
																}}>
																{configs.lastSyncStatus ??
																	'Not Available'}
															</Box>
														</Typography>

														<Typography
															variant="caption"
															color="text.secondary">
															<strong>
																Last Synced:
															</strong>{' '}
															{configs.lastSyncedAt
																? new Date(
																		configs.lastSyncedAt,
																	).toLocaleString(
																		'en-GB',
																		{
																			day: '2-digit',
																			month: 'short',
																			year: 'numeric',
																			hour: '2-digit',
																			minute: '2-digit',
																			hour12: true,
																		},
																	)
																: 'Never'}
														</Typography>
													</Stack>
												</Box>
											</Stack>

											<Stack spacing={1.5}>
												{/* Connection Status */}
												<Chip
													label={
														apiConnectCard.status
													}
													size="small"
													sx={{
														bgcolor:
															apiConnectCard.bg,
														color: apiConnectCard.color,
														fontWeight: 700,
														minWidth: 110,
													}}
												/>

												<Button
													onClick={onEditConfigs}
													size="small"
													startIcon={
														<Edit fontSize="small" />
													}
													sx={{
														backgroundColor: '#fff',
														color: dark,
														fontSize: '13px',
														marginLeft: '5px',
														fontWeight: 600,
														borderRadius: '8px',
														paddingX: '16px',
														textTransform: 'none',
														boxShadow: 'none',
														border: `1px solid ${dark}`,
													}}>
													Edit
												</Button>

												<Button
													onClick={removeConfigs}
													variant="outlined"
													color="error"
													size="small"
													startIcon={
														<Delete fontSize="small" />
													}
													sx={{
														textTransform: 'none',
														borderRadius: 2,
													}}>
													Remove
												</Button>
											</Stack>
										</Box>
									) : (
										<Card
											sx={{
												borderRadius: '18px',
												border: '1px solid #d8e8e8',
												boxShadow: 'none',
												alignItems: 'center',
												width: '100%',
											}}>
											<CardContent
												sx={{
													p: 3,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													minHeight: 50,
													color: '#6b8888',
													fontSize: 13,
													fontWeight: 500,
												}}>
												No APIs connected.
											</CardContent>
										</Card>
									)}
								</Stack>

								{showApiConfigForm && (
									<>
										<Box sx={{ mt: 3 }}>
											<Typography
												sx={{
													fontSize: 13,
													fontWeight: 700,
													color: '#334155',
													mb: 1.5,
												}}>
												{isApiConfigured
													? 'Edit integration'
													: 'Add new integration'}
											</Typography>

											<Grid
												container
												flexDirection={'column'}
												rowGap={1}>
												<FormField
													label="Base URL"
													placeholder="https://api.senzcare.com"
													value={newConfigs.baseUrl}
													onChange={e =>
														handleAPIConfigChange(
															'baseUrl',
															e.target.value,
														)
													}
													error={
														newConfigsError.baseUrl
													}
												/>
												{!isApiConfigured && (
													<FormField
														label="Client Type"
														placeholder="Organization"
														value={
															newConfigs.clientType
														}
														onChange={e =>
															handleAPIConfigChange(
																'clientType',
																e.target.value,
															)
														}
														error={
															newConfigsError.clientType
														}
													/>
												)}

												<Typography
													sx={{
														fontSize: 12,
														fontWeight: 500,
														color: '#334155',
													}}>
													Authentication
												</Typography>
												<TextField
													select
													size="small"
													fullWidth
													value={
														newConfigs.authType ||
														'Authentication Type'
													}
													onChange={e =>
														handleAPIConfigChange(
															'authType',
															e.target.value,
														)
													}
													sx={{
														'& .MuiInputBase-input':
															{
																fontSize: 12,
																'&::placeholder':
																	{
																		fontSize: 12,
																	},
															},
														'& .MuiSelect-select': {
															fontSize: '12px',
														},
													}}
													error={
														newConfigsError.authType
															?.status
													}>
													<MenuItem
														value="Authentication Type"
														disabled
														sx={{
															fontSize: '12px',
														}}>
														Authentication Type
													</MenuItem>
													<MenuItem value="BEARER">
														Bearer Token
													</MenuItem>

													<MenuItem value="BASIC">
														Basic Authentication
													</MenuItem>

													<MenuItem value="API_KEY">
														API Key
													</MenuItem>

													<MenuItem value="CUSTOM_HEADER">
														Custom Header
													</MenuItem>
												</TextField>
												{newConfigsError.authType
													?.status && (
													<Typography
														sx={{
															fontSize: 12,
															fontWeight: 500,
															color: 'red',
														}}>
														{
															newConfigsError
																.authType?.msg
														}
													</Typography>
												)}

												{/* Bearer */}

												{newConfigs.authType ===
													'BEARER' && (
													<Grid item xs={12}>
														<FormField
															type="password"
															label="Bearer Token"
															value={
																newConfigs.token
															}
															onChange={e =>
																handleAPIConfigChange(
																	'token',
																	e.target
																		.value,
																)
															}
														/>
													</Grid>
												)}

												{/* Basic */}

												{newConfigs.authType ===
													'BASIC' && (
													<>
														<FormField
															label="Username"
															value={
																newConfigs.username
															}
															onChange={e =>
																handleAPIConfigChange(
																	'username',
																	e.target
																		.value,
																)
															}
														/>

														<FormField
															type="password"
															label="Password"
															value={
																newConfigs.password
															}
															onChange={e =>
																handleAPIConfigChange(
																	'password',
																	e.target
																		.value,
																)
															}
														/>
													</>
												)}

												{/* API KEY */}

												{newConfigs.authType ===
													'API_KEY' && (
													<Grid item xs={12}>
														<FormField
															label="API Key"
															value={
																newConfigs.apiKey
															}
															onChange={e =>
																handleAPIConfigChange(
																	'apiKey',
																	e.target
																		.value,
																)
															}
														/>
													</Grid>
												)}

												{/* Custom Header */}

												{newConfigs.authType ===
													'CUSTOM_HEADER' && (
													<>
														<FormField
															label="Header Name"
															placeholder="X-API-KEY"
															value={
																newConfigs.headerName
															}
															onChange={e =>
																handleAPIConfigChange(
																	'headerName',
																	e.target
																		.value,
																)
															}
														/>

														<FormField
															fullWidth
															type="password"
															label="Header Value"
															value={
																newConfigs.headerValue
															}
															onChange={e =>
																handleAPIConfigChange(
																	'headerValue',
																	e.target
																		.value,
																)
															}
														/>
													</>
												)}
											</Grid>
										</Box>
										<Stack
											direction="row"
											spacing={1.5}
											mt={1.5}>
											<Button
												onClick={
													isApiConfigured
														? onUpdateExternalApiDetails
														: onStoreExternalApiDetails
												}
												variant="contained"
												disabled={isLoading}
												sx={{
													width: '40%',
													borderRadius: '12px',
													backgroundColor: teal,
													textTransform: 'none',
													fontWeight: 600,
													fontSize: '13px',
													boxShadow: 'none',
													'&:hover': {
														backgroundColor: teal,
														boxShadow: 'none',
													},
													color: dark,
													height: '35px',
												}}>
												{isApiConfigured
													? 'Update'
													: 'Onboard'}
											</Button>

											{isApiConfigured && (
												<Button
													onClick={onCancelConfigEdit}
													disabled={isLoading}
													sx={{
														backgroundColor: '#fff',
														color: dark,
														fontSize: '13px',
														marginLeft: '5px',
														fontWeight: 600,
														borderRadius: '12px',
														paddingX: '16px',
														textTransform: 'none',
														boxShadow: 'none',
														border: `1px solid ${dark}`,
														height: '35px',
													}}>
													Cancel
												</Button>
											)}
										</Stack>
									</>
								)}
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Box>
			<ToastBanner
				open={isLoading}
				message={loadingMsg}
				icon={<AutoAwesomeIcon color={'#FFFFFF'} />}
				isCompleted={isGenerationComplete}
			/>
		</MainGrid>
	);
}
